import { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { Spinner } from '../Spinner';
import { adminMe } from '../../api/adminClient';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { COLORS } from '../../constants/theme';

/**
 * Route guard for `/chancellery/*` (SF-112). Redirects to `/chancellery/login` when there is no
 * live in-memory admin token. When a token IS present (e.g. survived a dev hot-reload), verifies
 * it with `GET /api/admin/auth/me` on mount rather than trusting client-side expiry math alone --
 * an expired/invalid token clears the session and redirects too.
 */
export function RequireAdminAuth() {
  const { isAuthenticated, logout } = useAdminAuth();
  const [isVerifying, setIsVerifying] = useState(isAuthenticated);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsVerifying(false);
      return;
    }

    let cancelled = false;
    setIsVerifying(true);

    adminMe().then(({ data }) => {
      if (cancelled) {
        return;
      }
      if (data) {
        setVerified(true);
      } else {
        logout();
      }
      setIsVerifying(false);
    });

    return () => {
      cancelled = true;
    };
    // Re-verify only when authentication state flips (e.g. a fresh login) -- not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate to="/chancellery/login" replace />;
  }

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: COLORS.bg }}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (!verified) {
    return <Navigate to="/chancellery/login" replace />;
  }

  return <Outlet />;
}
